import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@reach/router"
import { Helmet } from "react-helmet"
import { tags } from "@tryghost/helpers"
import MetaData from "./metaData"

function JsonData({
  title: setTitle,
  description: setDescription,
  ghostData = {},
  pageType,
}) {
  const loadAllSettings = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
            slugs {
              blog
              author
              tag
            }
          }
        }

        allGhostSettings {
          edges {
            node {
              ...GhostSettingsFields
            }
          }
        }
      }
    `
  )

  const { pathname } = useLocation()
  const siteSettings = loadAllSettings.site.siteMetadata
  const ghostSettings = loadAllSettings.allGhostSettings.edges[0].node
  const canonical = `${siteSettings.siteUrl}${pathname}`
  const { slugs } = siteSettings

  const {
    mainTitle,
    mainDescription,
    ogImage,
    ogImageWidth,
    ogImageHeight,
    author,
    authorLinks,
  } = MetaData({
    title: setTitle,
    description: setDescription,
    ghostData,
    pageType,
  })

  const jsonData = {
    context: `https://schema.org`,
    publisher: {
      "@type": `Organization`,
      name: ghostSettings.title,
      url: `${siteSettings.siteUrl}/`,
      logo: {
        "@type": `ImageObject`,
        url: ghostSettings.logo,
        width: 870,
        height: 116,
      },
    },
    author: author
      ? {
          "@type": `Person`,
          name: author.name,
          image: author.profile_image
            ? {
                "@type": "ImageObject",
                url: author.profile_image,
                width: 300,
                height: 300,
              }
            : undefined,
          url: `${siteSettings.siteUrl}${slugs.blog}${slugs.author}/${author.slug}/`,
          sameAs: authorLinks !== [] ? authorLinks : undefined,
        }
      : undefined,
    url: canonical,
    keywords:
      ghostData.tags?.length > 0
        ? tags(ghostData, { visibility: "public" })
        : undefined,
    headline: mainTitle,
    datePublished: ghostData.published_at,
    dateModified: ghostData.updated_at,
    image: {
      "@type": `ImageObject`,
      url: ogImage,
      width: ogImageWidth,
      height: ogImageHeight,
    },
    description: mainDescription,
    mainEntityOfPage: {
      "@type": `WebPage`,
      "@id": `${siteSettings.siteUrl}/`,
    },
  }

  const typeWebSite = {
    "@context": jsonData.context,
    "@type": "WebSite",
    publisher: jsonData.publisher,
    url: jsonData.url,
    image: jsonData.image,
    mainEntityOfPage: jsonData.mainEntityOfPage,
    description: jsonData.description,
  }

  const typeArticle = {
    "@context": jsonData.context,
    "@type": "Article",
    publisher: jsonData.publisher,
    author: jsonData.author,
    headline: jsonData.headline,
    url: jsonData.url,
    datePublished: jsonData.datePublished,
    dateModified: jsonData.dateModified,
    image: jsonData.image,
    keywords: jsonData.keywords,
    description: jsonData.description,
    mainEntityOfPage: jsonData.mainEntityOfPage,
  }

  const typeSeries = {
    "@context": jsonData.context,
    "@type": "Series",
    url: jsonData.url,
    name: jsonData.headline,
    mainEntityOfPage: jsonData.mainEntityOfPage,
    description: jsonData.description,
  }

  const typePerson = {
    "@context": jsonData.context,
    "@type": "Person",
    name: jsonData.author?.name,
    image: jsonData.author?.image,
    url: jsonData.author?.url,
    sameAs: jsonData.author?.sameAs,
    mainEntityOfPage: jsonData.mainEntityOfPage,
    description: jsonData.description,
  }

  let jsonLd
  switch (pageType) {
    case "blogPage":
      if (ghostData.page) {
        jsonLd = typeArticle
      } else {
        jsonLd = typeWebSite
      }
      break
    case "blogByAuthor":
      jsonLd = typePerson
      break
    case "blogByTag":
      jsonLd = typeSeries
      break
    case "blogPost":
      jsonLd = typeArticle
      break
    default:
      jsonLd = typeWebSite
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd, undefined, 4)}
        </script>
      </Helmet>
    </>
  )
}

export default JsonData
